import z from "zod";

import { ErrorClassification } from "../../../sentry/classification";
import type { ErrorDetails } from "../domain";

const authErrorTypeValues = [
  "UNKNOWN",
  "TOKEN_NOT_FOUND",
  "SALT_NOT_FOUND",
  "INVITATION_NOT_FOUND",
  "USER_NOT_FOUND",
  "MISSING_IMPERSONATION_PERMISSION",
  "TOKEN_EXPIRED",
  "INVALID_API_KEY_SECRET",
  "JWT_INVALID",
  "PERSON_DISABLED",
  "OTP_NOT_FOUND",
  "OTP_EXPIRED",
  "OTP_INVALID_CODE",
  "OTP_MAX_ATTEMPTS_EXCEEDED",
  "OTP_DESTINATION_NOT_FOUND",
] as const;
const authErrorTypeSchema = z.enum(authErrorTypeValues);
const AuthErrorType = authErrorTypeSchema.enum;
type AuthErrorType = z.infer<typeof authErrorTypeSchema>;

export type AuthErrors =
  | { errorType: typeof AuthErrorType.UNKNOWN; data: undefined }
  | { errorType: typeof AuthErrorType.TOKEN_NOT_FOUND; data: { id: string } }
  | { errorType: typeof AuthErrorType.SALT_NOT_FOUND; data: { id: string } }
  | { errorType: typeof AuthErrorType.INVITATION_NOT_FOUND; data: { invitationId: string } }
  | { errorType: typeof AuthErrorType.USER_NOT_FOUND; data: { userId: string } }
  | { errorType: typeof AuthErrorType.MISSING_IMPERSONATION_PERMISSION; data: { userId: string } }
  | { errorType: typeof AuthErrorType.TOKEN_EXPIRED; data: { tokenId: string } }
  | { errorType: typeof AuthErrorType.INVALID_API_KEY_SECRET; data: { tokenId: string } }
  | { errorType: typeof AuthErrorType.JWT_INVALID; data: { authFailureReason: string } }
  | { errorType: typeof AuthErrorType.PERSON_DISABLED; data: { personId: string } }
  | { errorType: typeof AuthErrorType.OTP_NOT_FOUND; data: Record<string, never> }
  | { errorType: typeof AuthErrorType.OTP_EXPIRED; data: Record<string, never> }
  | { errorType: typeof AuthErrorType.OTP_INVALID_CODE; data: { attemptsRemaining: number } }
  | { errorType: typeof AuthErrorType.OTP_MAX_ATTEMPTS_EXCEEDED; data: Record<string, never> }
  | { errorType: typeof AuthErrorType.OTP_DESTINATION_NOT_FOUND; data: { channel: string } };

export const authErrorDetails = {
  UNKNOWN: { classification: ErrorClassification.UNEXPECTED_ERROR, sentryDescription: "Unknown Error" },
  TOKEN_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Token not found" },
  SALT_NOT_FOUND: { classification: ErrorClassification.NOT_FOUND_ERROR, sentryDescription: "Salt not found" },
  INVITATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Invitation not found",
  },
  USER_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "User not found",
  },
  MISSING_IMPERSONATION_PERMISSION: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Missing impersonation permission",
  },
  TOKEN_EXPIRED: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "Token expired",
  },
  INVALID_API_KEY_SECRET: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid API key checksum",
  },
  JWT_INVALID: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "JWT failed verification (signature, audience, expiration, or algorithm)",
  },
  PERSON_DISABLED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Person is disabled",
  },
  OTP_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "OTP not found",
  },
  OTP_EXPIRED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "OTP expired",
  },
  OTP_INVALID_CODE: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "Invalid OTP code",
  },
  OTP_MAX_ATTEMPTS_EXCEEDED: {
    classification: ErrorClassification.VALIDATION_ERROR,
    sentryDescription: "OTP max attempts exceeded",
  },
  OTP_DESTINATION_NOT_FOUND: {
    classification: ErrorClassification.NOT_FOUND_ERROR,
    sentryDescription: "No phone or email found for OTP delivery",
  },
} as const satisfies Record<AuthErrorType, ErrorDetails>;
